import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Trophy, 
  Swords,
  Calendar,
  Clock,
  MapPin,
  Users,
  Loader2,
  ArrowLeft,
  Send,
  Check,
  X,
  Target,
  Zap,
  Plus,
  Minus
} from "lucide-react";

const MatchesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("incoming");
  const [incomingChallenges, setIncomingChallenges] = useState([]);
  const [outgoingChallenges, setOutgoingChallenges] = useState([]);
  const [pendingResults, setPendingResults] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [clubMembers, setClubMembers] = useState([]);
  
  // Challenge form
  const [challengeForm, setChallengeForm] = useState({
    targetClubId: "",
    matchDate: "",
    matchTime: "",
    venue: "",
    format: "7x7",
    message: ""
  });
  
  // Result form
  const [resultForm, setResultForm] = useState({
    homeScore: 0,
    awayScore: 0,
    goals: []
  });

  const isAdmin = ["president", "manager", "captain"].includes(user?.clubRole);

  useEffect(() => {
    if (user?.clubId) {
      fetchChallenges();
      fetchClubs();
      fetchClubMembers();
    }
  }, [user?.clubId]);

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const [incoming, outgoing] = await Promise.all([
        axios.get(`${API}/matches/challenges/incoming`),
        axios.get(`${API}/matches/challenges/outgoing`)
      ]);
      setIncomingChallenges(incoming.data.challenges || []);
      setOutgoingChallenges(outgoing.data.challenges || []);
      
      // Kabul edilen maçları filtrele (sonuç girişi bekleyenler)
      const accepted = outgoing.data.challenges?.filter(c => c.status === "accepted") || [];
      setPendingResults(accepted);
    } catch (error) {
      console.error("Error fetching challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API}/clubs`);
      // Kendi kulübünü hariç tut
      setClubs((response.data.clubs || []).filter(c => c.id !== user?.clubId));
    } catch (error) {
      console.error("Error fetching clubs:", error);
    }
  };

  const fetchClubMembers = async () => {
    try {
      const response = await axios.get(`${API}/clubs/${user?.clubId}/members`);
      setClubMembers(response.data.members || []);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleAutoMatch = async () => {
    try {
      const response = await axios.post(`${API}/matches/auto-match`);
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleSendChallenge = async () => {
    if (!challengeForm.targetClubId || !challengeForm.matchDate || !challengeForm.matchTime) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }
    
    try {
      const params = new URLSearchParams({
        target_club_id: challengeForm.targetClubId,
        match_date: challengeForm.matchDate,
        match_time: challengeForm.matchTime,
        format: challengeForm.format
      });
      if (challengeForm.venue) params.append("venue", challengeForm.venue);
      if (challengeForm.message) params.append("message", challengeForm.message);
      
      const response = await axios.post(`${API}/matches/challenge?${params.toString()}`);
      if (response.data.success) {
        toast.success("Maç teklifi gönderildi!");
        setShowChallengeDialog(false);
        setChallengeForm({ targetClubId: "", matchDate: "", matchTime: "", venue: "", format: "7x7", message: "" });
        fetchChallenges();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Teklif gönderilemedi");
    }
  };

  const handleRespondChallenge = async (challengeId, accept) => {
    try {
      const response = await axios.post(`${API}/matches/challenges/${challengeId}/respond?accept=${accept}`);
      if (response.data.success) {
        toast.success(accept ? "Teklif kabul edildi!" : "Teklif reddedildi");
        fetchChallenges();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleSubmitResult = async () => {
    if (!selectedMatch) return;
    
    try {
      const response = await axios.post(`${API}/matches/${selectedMatch.id}/result`, null, {
        params: {
          home_score: resultForm.homeScore,
          away_score: resultForm.awayScore
        }
      });
      
      if (response.data.success) {
        toast.success(response.data.isFinalized ? "Maç sonucu kesinleşti!" : "Sonuç kaydedildi, diğer takımın onayı bekleniyor");
        setShowResultDialog(false);
        setSelectedMatch(null);
        setResultForm({ homeScore: 0, awayScore: 0, goals: [] });
        fetchChallenges();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sonuç kaydedilemedi");
    }
  };

  const addGoal = () => {
    setResultForm(prev => ({
      ...prev,
      goals: [...prev.goals, { scorerId: "", scorerName: "", assistId: "", assistName: "", minute: "" }]
    }));
  };

  const removeGoal = (index) => {
    setResultForm(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const updateGoal = (index, field, value) => {
    setResultForm(prev => ({
      ...prev,
      goals: prev.goals.map((g, i) => i === index ? { ...g, [field]: value } : g)
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!user?.clubId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-12 text-center">
          <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold mb-2">Kulüp Üyeliği Gerekli</h2>
          <p className="text-gray-500 mb-4">Maç eşleşmesi için bir kulübe üye olmalısınız</p>
          <Button onClick={() => navigate("/clubs")}>Kulüplere Git</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Başlık */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Swords className="h-6 w-6 text-emerald-600" />
                Maç Eşleşmeleri
              </h1>
              <p className="text-gray-500">{user?.clubName}</p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleAutoMatch}
                className="gap-2"
              >
                <Zap className="h-4 w-4" />
                Otomatik Eşleş (5💰)
              </Button>
              <Button 
                onClick={() => setShowChallengeDialog(true)}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="h-4 w-4" />
                Teklif Gönder (20💰)
              </Button>
            </div>
          )}
        </div>

        {/* Kredi Bilgisi */}
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-700">
                <Trophy className="h-5 w-5" />
                <span>Krediniz:</span>
                <span className="font-bold text-lg">{user?.credits || 0} 💰</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/credits")}>
                Kredi Al
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="incoming" className="gap-2">
              Gelen Teklifler
              {incomingChallenges.length > 0 && (
                <Badge className="bg-red-500 ml-1">{incomingChallenges.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="outgoing">Gönderilen</TabsTrigger>
            <TabsTrigger value="results">Sonuç Gir</TabsTrigger>
          </TabsList>

          {/* Gelen Teklifler */}
          <TabsContent value="incoming">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : incomingChallenges.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Swords className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Gelen maç teklifi yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {incomingChallenges.map((challenge) => (
                  <Card key={challenge.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-14 w-14">
                            <AvatarImage src={challenge.challengerClubLogo} />
                            <AvatarFallback className="bg-emerald-100 text-emerald-700">
                              {challenge.challengerClubName?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-bold text-lg">{challenge.challengerClubName}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {challenge.matchDate}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {challenge.matchTime}
                              </span>
                              {challenge.venue && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  {challenge.venue}
                                </span>
                              )}
                            </div>
                            {challenge.message && (
                              <p className="text-sm text-gray-600 mt-2 italic">"{challenge.message}"</p>
                            )}
                          </div>
                        </div>
                        
                        {isAdmin && (
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              onClick={() => handleRespondChallenge(challenge.id, false)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reddet
                            </Button>
                            <Button 
                              onClick={() => handleRespondChallenge(challenge.id, true)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Kabul Et
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Gönderilen Teklifler */}
          <TabsContent value="outgoing">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : outgoingChallenges.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Send className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Gönderilen maç teklifi yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {outgoingChallenges.map((challenge) => (
                  <Card key={challenge.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-14 w-14">
                            <AvatarFallback className="bg-blue-100 text-blue-700">
                              {challenge.targetClubName?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-bold text-lg">{challenge.targetClubName}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {challenge.matchDate}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {challenge.matchTime}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <Badge className={
                          challenge.status === "accepted" ? "bg-green-500" :
                          challenge.status === "rejected" ? "bg-red-500" :
                          "bg-amber-500"
                        }>
                          {challenge.status === "accepted" ? "Kabul Edildi" :
                           challenge.status === "rejected" ? "Reddedildi" :
                           "Bekliyor"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sonuç Girişi */}
          <TabsContent value="results">
            {pendingResults.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Target className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Sonuç girilecek maç yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingResults.map((match) => (
                  <Card key={match.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{match.challengerClubName}</span>
                            <span className="text-gray-400">vs</span>
                            <span className="font-bold">{match.targetClubName}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {match.matchDate} - {match.matchTime}
                          </p>
                        </div>
                        
                        {isAdmin && (
                          <Button 
                            onClick={() => {
                              setSelectedMatch(match);
                              setShowResultDialog(true);
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Target className="h-4 w-4 mr-2" />
                            Sonuç Gir
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Maç Teklifi Dialog */}
      <Dialog open={showChallengeDialog} onOpenChange={setShowChallengeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-emerald-600" />
              Maç Teklifi Gönder
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Rakip Kulüp *</Label>
              <Select 
                value={challengeForm.targetClubId} 
                onValueChange={(v) => setChallengeForm(prev => ({ ...prev, targetClubId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kulüp seçin" />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map((club) => (
                    <SelectItem key={club.id} value={club.id}>
                      {club.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Maç Tarihi *</Label>
                <Input 
                  type="date" 
                  value={challengeForm.matchDate}
                  onChange={(e) => setChallengeForm(prev => ({ ...prev, matchDate: e.target.value }))}
                />
              </div>
              <div>
                <Label>Maç Saati *</Label>
                <Input 
                  type="time" 
                  value={challengeForm.matchTime}
                  onChange={(e) => setChallengeForm(prev => ({ ...prev, matchTime: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Saha (Opsiyonel)</Label>
              <Input 
                placeholder="Maç yapılacak saha"
                value={challengeForm.venue}
                onChange={(e) => setChallengeForm(prev => ({ ...prev, venue: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Format</Label>
              <Select 
                value={challengeForm.format} 
                onValueChange={(v) => setChallengeForm(prev => ({ ...prev, format: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5x5">5v5</SelectItem>
                  <SelectItem value="6x6">6v6</SelectItem>
                  <SelectItem value="7x7">7v7</SelectItem>
                  <SelectItem value="11x11">11v11</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Mesaj (Opsiyonel)</Label>
              <Textarea 
                placeholder="Rakip takıma mesaj..."
                value={challengeForm.message}
                onChange={(e) => setChallengeForm(prev => ({ ...prev, message: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-amber-600 font-medium">Maliyet: 20 💰</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowChallengeDialog(false)}>
                  İptal
                </Button>
                <Button 
                  onClick={handleSendChallenge}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={(user?.credits || 0) < 20}
                >
                  Gönder
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sonuç Girişi Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-600" />
              Maç Sonucu Gir
            </DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-6">
              {/* Skor */}
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="font-bold text-lg mb-2">{selectedMatch.challengerClubName}</p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setResultForm(prev => ({ ...prev, homeScore: Math.max(0, prev.homeScore - 1) }))}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-4xl font-bold w-12 text-center">{resultForm.homeScore}</span>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setResultForm(prev => ({ ...prev, homeScore: prev.homeScore + 1 }))}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <span className="text-2xl text-gray-400">-</span>
                
                <div className="text-center">
                  <p className="font-bold text-lg mb-2">{selectedMatch.targetClubName}</p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setResultForm(prev => ({ ...prev, awayScore: Math.max(0, prev.awayScore - 1) }))}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-4xl font-bold w-12 text-center">{resultForm.awayScore}</span>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setResultForm(prev => ({ ...prev, awayScore: prev.awayScore + 1 }))}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-center text-gray-500">
                * Her iki takımın da aynı skoru girmesi gerekmektedir
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmitResult}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Sonucu Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MatchesPage;
